import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { ContainerTriagePanel } from '../../components/receipts'
import { ProcessableCollectionsSection } from './processing/ProcessableCollectionsSection'
import { IngestionRunsSection } from './processing/IngestionRunsSection'
import { RunDetailPanel } from './processing/RunDetailPanel'
import {
  CorruptDialog,
  MutationErrorBanner,
  ProcessingHeader,
} from './case-processing-tab/case-processing-tab.parts'
import { useCaseProcessingTab } from './case-processing-tab/use-case-processing-tab'

interface CaseProcessingTabProps {
  caseId: string
}

export function CaseProcessingTab({ caseId }: CaseProcessingTabProps): ReactElement {
  const { t } = useTranslation('cases')
  const state = useCaseProcessingTab({ caseId, t })

  return (
    <div className="space-y-8">
      <ProcessingHeader t={t} />
      {state.mutationError && (
        <MutationErrorBanner message={state.mutationError} onDismiss={state.clearError} />
      )}

      <section>
        <h3 className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-300">
          {t('detail.processing.processableCollections')}
        </h3>
        <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
          <ProcessableCollectionsSection
            collections={state.collections}
            loading={state.collectionsLoading}
            error={state.collectionsError}
            isRowBusy={state.isRowBusy}
            onProcess={(receiptId) => void state.handleProcess(receiptId)}
            onTriage={state.openTriage}
          />
        </div>
      </section>

      <section>
        <h3 className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-300">
          {t('detail.processing.ingestionRuns')}
        </h3>
        <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
          <IngestionRunsSection
            runs={state.runs}
            loading={state.runsLoading}
            error={state.runsError}
            selectedRunId={state.selectedRunId}
            onSelectRun={state.setSelectedRunId}
          />
          {state.selectedRunId && (
            <RunDetailPanel
              caseId={caseId}
              runId={state.selectedRunId}
              onVerify={(runId) => void state.handleVerify(runId)}
              onFlagCorrupt={state.handleFlagCorruptRequest}
              verifying={state.verifying}
              flagging={state.flagging}
            />
          )}
        </div>
      </section>

      {state.showCorruptDialog && (
        <CorruptDialog
          corruptReason={state.corruptReason}
          setCorruptReason={state.setCorruptReason}
          flagging={state.flagging}
          onCancel={state.closeCorruptDialog}
          onConfirm={() => void state.handleFlagCorruptConfirm()}
          t={t}
        />
      )}

      {state.triageReceiptId && (
        <ContainerTriagePanel
          caseId={caseId}
          receiptId={state.triageReceiptId}
          onClose={state.closeTriage}
        />
      )}
    </div>
  )
}
